import React, { useState, useEffect } from 'react'
import Breadcrum from '../../components/breadcrum'
import { useForm, useFieldArray } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { createFinancial, getFinancialById } from '../../api/financialApi'
import { FaArrowLeft } from 'react-icons/fa6'
import { FaPlusCircle } from 'react-icons/fa'
// import { validateCin } from '../../api/dashboardApi'

const AddFinancialReport = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const isEditMode = Boolean(id)
  const [imagePreview, setImagePreview] = useState(null)
  const [uploadProgress, setUploadProgress] = useState(0)

  // const [cinValidation, setCinValidation] = useState({
  //   isValidating: false,
  //   isValid: null,
  //   companyName: '',
  //   error: '',
  // })

  useEffect(() => {
    document.title = 'Add Financial Report'
  }, [isEditMode])

  const {
    register,
    watch,
    handleSubmit,
    setValue,
    reset,
    setError,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: {
      companyName: '',
      cin: '',
      financialYear: '',
      companyLogo: null,
      reports: [
        {
          reportTitle: '',
          reportDesc: '',
          reportPrice: '',
          report: null,
        },
      ],
    },
  })

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'reports',
  })

  // Updated React component validation function
  // const validateCIN = async (cinNumber) => {
  //   if (!cinNumber || cinNumber.length !== 21) {
  //     setCinValidation({
  //       isValidating: false,
  //       isValid: false,
  //       companyName: '',
  //       error: 'Invalid CIN format',
  //     })
  //     return
  //   }

  //   setCinValidation((prev) => ({ ...prev, isValidating: true }))

  //   try {
  //     setCinValidation((prev) => ({
  //       ...prev,
  //       isValidating: true,
  //     }))

  //     const res = await validateCin(cinNumber)
  //     console.log('Validation result:', res)

  //     if (res?.company) {
  //       setCinValidation({
  //         isValidating: false,
  //         isValid: true,
  //         companyName: res.company,
  //         error: '',
  //       })
  //     } else {
  //       setCinValidation({
  //         isValidating: false,
  //         isValid: false,
  //         companyName: '',
  //         error: res?.error || 'CIN not found',
  //       })
  //     }
  //   } catch (error) {
  //     console.error('CIN validation error:', error)
  //     setCinValidation({
  //       isValidating: false,
  //       isValid: false,
  //       companyName: '',
  //       error: error?.response?.data?.error || 'Error validating CIN',
  //     })
  //   }
  // }

  // // 3. Add this useEffect to watch for CIN changes
  // useEffect(() => {
  //   const cinValue = watch('cin')
  //   if (cinValue && cinValue.length === 21) {
  //     const debounceTimer = setTimeout(() => {
  //       validateCIN(cinValue)
  //     }, 500) // 500ms delay to avoid too many API calls

  //     return () => clearTimeout(debounceTimer)
  //   } else if (cinValue && cinValue.length > 0) {
  //     setCinValidation({
  //       isValidating: false,
  //       isValid: null,
  //       companyName: '',
  //       error: '',
  //     })
  //   }
  // }, [watch('cin')])

  // Query for company basic info
  const {
    data: financialData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['financialData', id],
    queryFn: () => getFinancialById(id),
    enabled: !!id,
    onSuccess: () => {
      populateData()
    },
  })

  const populateData = (data) => {
    if (!data) {
      return
    }

    setValue('companyName', data.companyName || '')
    setValue('cin', data.cin || '')

    if (data.companyLogoUrl) {
      setImagePreview(data.companyLogoUrl)
    }
  }

  useEffect(() => {
    if (financialData) {
      populateData(financialData)
    }
  }, [financialData])

  // Create mutation
  const createMutation = useMutation({
    mutationFn: ({ formData }) =>
      createFinancial(formData, (percent) => setUploadProgress(percent)),
    onSuccess: (data) => {
      setUploadProgress(0)
      reset()
      toast.success('Report added successfully!')
      navigate('/dealdashboard/financial')
    },
    onError: (error) => {
      setUploadProgress(0)
      console.error('Error details:', error)
      toast.error(error.response?.data?.message || 'Failed to add report')
    },
  })

  const handlePdfChange = (e, index) => {
    const files = e.target.files
    if (files && files.length > 0) {
      setValue(`reports.${index}.report`, files)
    }
  }

  const image = watch('companyLogo')
  useEffect(() => {
    if (image && image.length > 0) {
      const file = image[0]
      const preview = URL.createObjectURL(file)
      setImagePreview(preview)

      return () => URL.revokeObjectURL(preview)
    }
  }, [image])

  const handleImageChange = (e) => {
    const files = e.target.files
    if (files && files.length > 0) {
      setValue('companyLogo', files)
    }
  }

  const addColumn = () => {
    append({
      reportTitle: '',
      reportDesc: '',
      reportPrice: '',
      report: null,
    })
  }

  const removeColumn = (index) => {
    if (fields.length > 1) {
      remove(index)
    }
  }

  const onSubmit = (data) => {
    // if (!cinValidation.isValid) {
    //   setError('cin', {
    //     type: 'manual',
    //     message: 'Please enter a correct CIN number',
    //   })
    //   toast.error('Please enter a correct CIN number')
    //   return
    // }

    const formData = new FormData()

    // Append basic fields
    formData.append('cin', data.cin)
    formData.append('companyName', data.companyName)
    formData.append('financialYear', data.financialYear)

    // Check if company logo exists before appending
    if (data.companyLogo?.length > 0) {
      formData.append('companyLogo', data.companyLogo[0])
    }

    // Append report data
    data.reports.forEach((report, index) => {
      if (report.report?.length > 0) {
        formData.append(`report`, report.report[0])
      }
      formData.append(`reportTitles`, report.reportTitle)
      formData.append(`reportPrices`, parseInt(report.reportPrice) || 0)
      formData.append(`reportDescs`, report.reportDesc)
    })

    // Log FormData contents for debugging
    console.log('FormData contents:')
    for (let [key, value] of formData.entries()) {
      console.log(key, value)
    }

    createMutation.mutate({ formData })
  }

  return (
    <>
      <Breadcrum
        heading="Financial Reports"
        subheading="Add Financial Report"
      />

      {uploadProgress > 0 && (
        <div className="w-full bg-gray-200 rounded-full h-3 mt-4 fixed top-[60px]">
          <div
            className="bg-green-700 h-3 rounded-full transition-all duration-200"
            style={{ width: `${uploadProgress}%` }}
          ></div>
        </div>
      )}

      {uploadProgress > 0 && (
        <p className="text-sm mt-1 font-medium text-white fixed top-[65px]">
          Uploading… {uploadProgress}%
        </p>
      )}

      <button
        type="button"
        onClick={() => navigate(-1)}
        className="flex justify-center items-center bg-gray-500 p-2 text-white rounded sticky -top-[15px]"
      >
        <FaArrowLeft />
        &nbsp; Back
      </button>

      <div className="max-w-8xl text-left mx-auto px-4 py-6">
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Company Information Section */}
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div className="bg-white p-6 rounded-xl shadow">
              <h2 className="font-semibold text-gray-800 mb-2">Company Name</h2>
              <input
                type="text"
                placeholder="Enter company name"
                readOnly={isEditMode}
                className={`border ${
                  errors.companyName ? 'border-red-500' : 'border-gray-300'
                } rounded-lg p-3 w-full text-base focus:border-orange-500 focus:ring-orange-500 focus:outline-none`}
                {...register('companyName', {
                  required: 'Company name is required',
                })}
              />
              {errors.companyName && (
                <p className="mt-1 text-red-500 text-sm">
                  {errors.companyName.message}
                </p>
              )}
            </div>

            <div className="bg-white p-6 rounded-xl shadow">
              <h2 className="font-semibold text-gray-800 mb-2">
                Company CIN Number
              </h2>

              <input
                type="text"
                placeholder="Enter CIN number"
                readOnly={isEditMode}
                className={`border ${
                  errors.cin ? 'border-red-500' : 'border-gray-300'
                } rounded-lg p-3 w-full text-base focus:border-orange-500 focus:ring-orange-500 focus:outline-none`}
                {...register('cin', {
                  required: 'CIN number is required',
                  pattern: {
                    value: /^[A-Z]{1}[0-9]{5}[A-Z]{2}[0-9]{4}[A-Z]{3}[0-9]{6}$/,
                    message: 'Please enter a valid CIN number format',
                  },
                })}
              />
              {errors.cin && (
                <p className="mt-1 text-red-500 text-sm">
                  {errors.cin.message}
                </p>
              )}
              {/* CIN Validation Status */}
              {/* {cinValidation.isValidating && (
                <div className="flex items-center mt-2 text-blue-600">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                  <span className="text-sm">Validating CIN...</span>
                </div>
              )}

              {cinValidation.isValid === true && (
                <div className="flex items-center mt-2 text-green-600">
                  <svg
                    className="h-4 w-4 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span className="text-sm font-medium">
                    {cinValidation.companyName}
                  </span>
                </div>
              )}

              {cinValidation.isValid === false && (
                <div className="flex items-center mt-2 text-red-600">
                  <svg
                    className="h-4 w-4 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                  <span className="text-sm">{cinValidation.error}</span>
                </div>
              )} */}
            </div>

            <div className="bg-white p-6 rounded-xl shadow">
              <h2 className="font-semibold text-gray-800 mb-2">
                Year Of Report
              </h2>
              <input
                type="text"
                placeholder="Enter report year (e.g., 2023-24)"
                className={`border ${
                  errors.financialYear ? 'border-red-500' : 'border-gray-300'
                } rounded-lg p-3 w-full text-base focus:border-orange-500 focus:ring-orange-500 focus:outline-none`}
                {...register('financialYear', {
                  required: 'Financial year is required',
                })}
              />
              {errors.financialYear && (
                <p className="mt-1 text-red-500 text-sm">
                  {errors.financialYear.message}
                </p>
              )}
            </div>

            <div className="bg-white p-6 rounded-xl shadow">
              <h2 className="font-semibold text-gray-800 mb-2">Company Logo</h2>
              {imagePreview && (
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="mt-4 max-h-64 object-cover rounded-lg border border-gray-200"
                />
              )}
              {isEditMode === false && (
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="mt-3 block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-orange-50 file:text-orange-600 hover:file:bg-orange-100"
                  {...register('companyLogo', {
                    required: isEditMode ? false : 'Company logo is required',
                  })}
                />
              )}
              {errors.companyLogo && (
                <p className="mt-1 text-red-500 text-sm">
                  {errors.companyLogo.message}
                </p>
              )}
            </div>
          </div>

          {/* Reports Section */}
          <div className="mb-6">
            <div className="flex justify-start items-center mb-4">
              <h2 className="text-xl font-bold text-gray-800">Reports</h2>
              <p className="ml-4 text-gray-600">
                (You can add multiple reports)
              </p>
            </div>

            {fields.map((field, index) => (
              <div
                key={field.id}
                className="mb-6 p-6 bg-gray-50 rounded-xl shadow"
              >
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-700">
                    Report #{index + 1}
                  </h3>
                  {fields.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeColumn(index)}
                      className="bg-red-500 hover:bg-red-600 text-white font-medium px-3 py-1 rounded text-sm transition-all"
                    >
                      Remove
                    </button>
                  )}
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <h4 className="font-semibold text-gray-800 mb-2">
                      Report Title
                    </h4>
                    <input
                      type="text"
                      placeholder="Enter report title"
                      className={`border ${
                        errors.reports?.[index]?.reportTitle
                          ? 'border-red-500'
                          : 'border-gray-300'
                      } rounded-lg p-3 w-full text-base focus:border-orange-500 focus:ring-orange-500 focus:outline-none`}
                      {...register(`reports.${index}.reportTitle`, {
                        required: 'Report title is required',
                      })}
                    />
                    {errors.reports?.[index]?.reportTitle && (
                      <p className="mt-1 text-red-500 text-sm">
                        {errors.reports[index].reportTitle.message}
                      </p>
                    )}
                  </div>

                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <h4 className="font-semibold text-gray-800 mb-2">
                      Report Description
                    </h4>
                    <textarea
                      placeholder="Enter report description"
                      className={`border ${
                        errors.reports?.[index]?.reportDesc
                          ? 'border-red-500'
                          : 'border-gray-300'
                      } rounded-lg p-3 w-full text-base focus:border-orange-500 focus:ring-orange-500 focus:outline-none resize-vertical min-h-[120px]`}
                      {...register(`reports.${index}.reportDesc`, {
                        required: 'Report description is required',
                      })}
                    />
                    {errors.reports?.[index]?.reportDesc && (
                      <p className="mt-1 text-red-500 text-sm">
                        {errors.reports[index].reportDesc.message}
                      </p>
                    )}
                  </div>

                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <h4 className="font-semibold text-gray-800 mb-2">
                      Report Price (₹)
                    </h4>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      placeholder="Enter report price"
                      className={`border ${
                        errors.reports?.[index]?.reportPrice
                          ? 'border-red-500'
                          : 'border-gray-300'
                      } rounded-lg p-3 w-full text-base focus:border-orange-500 focus:ring-orange-500 focus:outline-none`}
                      {...register(`reports.${index}.reportPrice`, {
                        required: 'Report price is required',
                        min: {
                          value: 0,
                          message: 'Price must be greater than or equal to 0',
                        },
                      })}
                    />
                    {errors.reports?.[index]?.reportPrice && (
                      <p className="mt-1 text-red-500 text-sm">
                        {errors.reports[index].reportPrice.message}
                      </p>
                    )}
                  </div>

                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <h4 className="font-semibold text-gray-800 mb-2">
                      Report File (PDF)
                    </h4>
                    {watch(`reports.${index}.report`) &&
                      watch(`reports.${index}.report`).length > 0 && (
                        <div className="mt-4">
                          <object
                            data={URL.createObjectURL(
                              watch(`reports.${index}.report`)[0]
                            )}
                            type="application/pdf"
                            className="w-full h-48 rounded border border-gray-200"
                          >
                            <p className="text-gray-600 text-sm p-4">
                              Your browser does not support PDF preview.{' '}
                              <a
                                href={URL.createObjectURL(
                                  watch(`reports.${index}.report`)[0]
                                )}
                                className="text-orange-500 hover:underline"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                Download PDF
                              </a>
                            </p>
                          </object>
                        </div>
                      )}
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={(e) => handlePdfChange(e, index)}
                      className="mt-3 block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-orange-50 file:text-orange-600 hover:file:bg-orange-100"
                      {...register(`reports.${index}.report`, {
                        required: 'Report file is required',
                      })}
                    />
                    {errors.reports?.[index]?.report && (
                      <p className="mt-1 text-red-500 text-sm">
                        {errors.reports[index].report.message}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {/* add report */}
            <div className="flex justify-end items-center mb-4">
              <button
                type="button"
                onClick={addColumn}
                className="bg-blue-500 hover:bg-blue-600 text-white font-medium px-4 py-3 rounded-md shadow-md transition-all"
              >
                Report <FaPlusCircle className="inline ml-2" />
              </button>
            </div>
          </div>

          {/* Submit Button */}
          <div className="mt-8 text-center">
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 text-white text-lg font-medium px-8 py-3 rounded-lg shadow-md transition-all disabled:cursor-not-allowed"
            >
              {createMutation.isPending ? 'Adding Report...' : 'Add Report'}
            </button>
          </div>
        </form>
      </div>
    </>
  )
}

export default AddFinancialReport
